<script lang="ts">
  import Button from '../../../components/button/Button.svelte'
  import UpdateProjectName from '../buttons/UpdateProjectName.svelte'
  import { useProjectQueries } from '../../../hooks/project'
  import { useProjectGcodeQuery } from '../../../queries'
  import i18n from '../../../i18n'
  import NavbarUserMenu from '../../../components/navbar-user-menu/NavbarUserMenu.svelte'
  import Tooltip from '../../../components/tooltip/Tooltip.svelte'
  import { LifeBuoy } from '@lucide/svelte'
  import { getContext } from 'svelte'
  import { get, type Writable } from 'svelte/store'

  type Props = {
    printerId: string
  }

  const { printerId }: Props = $props()
  const { downloadPrinterGcodeMutation } = useProjectQueries()

  // Shares the exact cache entry the preview polls, so the download button only
  // enables once that gcode is ready — and then reuses it instead of re-generating.
  const projectId = get(getContext<Writable<string | undefined>>('projectId'))
  /* svelte-ignore state_referenced_locally */
  const gcodeQuery = useProjectGcodeQuery(projectId, printerId)
</script>

<header class="container">
  <div class="name">
    <UpdateProjectName />
  </div>
  <div class="actions">
    <div class="section-container empty"></div>
    <div class="section-container">
      <div class="save-button-container">
        {#if downloadPrinterGcodeMutation.isPending}
          <Button disabled={downloadPrinterGcodeMutation.isPending} size="sm">
            {$i18n.t('projectPreview.navbar.downloadGcode.pending')}
          </Button>
        {:else}
          <Button
            disabled={gcodeQuery.data?.status !== 'ready'}
            onclick={() => downloadPrinterGcodeMutation.mutate(printerId)}
            size="sm"
          >
            {$i18n.t('projectPreview.navbar.downloadGcode.idle')}
          </Button>
        {/if}
      </div>
      <Tooltip title={$i18n.t('navbar.documentation.tooltip')}>
        <a
          class="navbar-button"
          href="https://docs.studio.constructions-3d.com"
          target="_blank"
        >
          <LifeBuoy />
        </a>
      </Tooltip>

      <NavbarUserMenu />
    </div>
  </div>
</header>

<style lang="scss">
  .container {
    align-items: stretch;
    border-bottom: 1px solid var(--grid-stroke);
    display: flex;
    flex-direction: row;
    flex-shrink: 0;

    .name {
      display: flex;
      border-right: 1px solid var(--grid-stroke);
      min-height: 4rem;
      width: 17rem;
    }

    .actions {
      flex: 1;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: stretch;

      .section-container {
        border-right: 1px solid var(--grid-stroke);
        display: flex;
        align-items: stretch;
        justify-content: center;
        flex-direction: row;

        &.empty {
          padding: 0;
          border: 0;
        }

        .save-button-container {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 1rem;
          width: 100%;
          border-right: 1px solid var(--grid-stroke);
        }

        :global(.tooltip-container) {
          display: flex;
        }

        :global(.tooltip-content) {
          display: flex;
        }

        .navbar-button {
          align-items: center;
          background: none;
          border: 0;
          border-right: 1px solid var(--grid-stroke);
          color: inherit;
          cursor: pointer;
          display: flex;
          justify-content: center;
          padding: 0 1rem;
          transition: all 0.3s ease;

          &:hover {
            background-color: var(--input-background);
          }
        }
      }
    }

    :global(> button) {
      width: fit-content;
      margin-left: auto;
      margin-right: 1rem;
    }
  }
</style>
